/**
 * src/modules/guardian-home-rituales.js
 *
 * Guardian que detecta rutas con sesión activa de grupo (/, /g/<codigo>) y
 * reemplaza el botón "Rituales del grupo" del home por un embed inline de la
 * vista /rituales/<codigo>. Resuelve DT-3 (sub-fase 3.11) y DT-5 (race FIFO
 * de listeners hashchange, sub-fase 3.11 fix).
 *
 * Migrado del IIFE inline de index.html en sub-fase 3.16.
 *
 * FASE 4 · Bug #1 (skeleton + caché) — async-aware:
 *   F1.3.d1 volvió `renderRitualesGrupo` ASYNC. El guardian original asumía
 *   render síncrono: pintaba un placeholder, lo "robaba" como vista final y
 *   la vista real terminaba pisando #app-root → el home quedaba en blanco.
 *   Ahora `embedRituales`:
 *     1. Inyecta un skeleton en el slot del embed APENAS aparece el home.
 *     2. Llama `renderRitualesGrupo(..., { target: slot })` — la vista se
 *        pinta DENTRO del slot, nunca sobre #app-root: el home no se pisa.
 *     3. Al resolver la promesa, el contenido real reemplaza al skeleton
 *        en el lugar (sin parpadeo, sin texto "Cargando…").
 *   La caché de `core/rituales.js` (TTL 60 s) hace que el 2.º embed dentro
 *   de la ventana sea instantáneo.
 *
 * Mecánica:
 *   - hashchange + load + setTimeout(0) inicial disparan patchProfileRoute.
 *   - patchProfileRoute detecta el código del grupo activo y delega en
 *     watchHomeRituals.
 *   - watchHomeRituals registra un MutationObserver sobre #app-root que
 *     dispara embedRituales mientras el embed (data-rituales-grupo-clone) no
 *     esté presente.
 *
 * El módulo se autoinicializa al cargarse (registra listeners + kick-off).
 * NO exporta funciones públicas (el skeleton vive en ./rituales-skeleton.js).
 */

import { renderRitualesGrupo } from './rituales.js';
import { buildRitualesSkeleton, buildRitualesError } from './rituales-skeleton.js';

const retargetEmbeddedRituals = (codigo, activeTab = 'arengas') => {
    const clone = document.querySelector('[data-rituales-grupo-clone]');
    if (!clone) return;
    clone.querySelectorAll('.arenga-open').forEach((button) => {
        const cleanButton = button.cloneNode(true);
        button.replaceWith(cleanButton);
        cleanButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            embedRituales(codigo, 'arengas', { arengaId: cleanButton.dataset.id });
        });
    });
    // NOTA: `.arenga-save` NO se neutraliza. El doc Arengas v1.3 §7/regla 10
    // permite guardar cualquier arenga (incluida la propia) desde cualquier
    // vista. Su handler real (rituales.js) ya re-renderiza embed-safe vía
    // reRenderRituales, así que se deja vivo en el home embebido. Sólo se
    // neutraliza `.arenga-react` (reacciones no operan en el preview embebido).
    clone.querySelectorAll('.arenga-react').forEach((button) => {
        const cleanButton = button.cloneNode(true);
        button.replaceWith(cleanButton);
        cleanButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
        });
    });
    clone.querySelectorAll('[onclick]').forEach((element) => {
        const action = element.getAttribute('onclick') || '';
        // Preservar overlays con guard de propagación (`event.target === this`).
        // Sin este skip, cualquier click interno bubble al overlay reescrito y
        // dispara `embedRituales` sin `?crear=1` → el modal se desmonta.
        // Aplica al modal "Crear arenga" (rituales.js:405) y al modal de
        // detalle de arenga (rituales.js:324). Bug latente desde sub-fase 3.11.
        if (action.includes('event.target')) return;
        if (action.includes(`/rituales/${codigo}/arengas`)) {
            element.removeAttribute('onclick');
            element.addEventListener('click', () => embedRituales(codigo, 'arengas'));
        } else if (action.includes(`/rituales/${codigo}/cabalas`)) {
            element.removeAttribute('onclick');
            element.addEventListener('click', () => embedRituales(codigo, 'cabalas'));
        } else if (action.includes(`/rituales/${codigo}/promesas`)) {
            element.removeAttribute('onclick');
            element.addEventListener('click', () => embedRituales(codigo, 'promesas'));
        } else if (action.includes(`/g/${codigo}`)) {
            element.remove();
        }
    });
    const createButton = clone.querySelector('#btnMockArenga');
    if (createButton) {
        const cleanCreateButton = createButton.cloneNode(true);
        createButton.replaceWith(cleanCreateButton);
        cleanCreateButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            embedRituales(codigo, activeTab, activeTab === 'arengas' ? { crear: true } : {});
        });
    }
};

const stripEmbeddedRitualHeader = (ritualView, codigo) => {
    const children = Array.from(ritualView.children);
    const groupHeader = children.find((child) => child.textContent.includes('INVITAR AMIGOS') || child.textContent.includes('Otro grupo') || child.textContent.includes('en este grupo'));
    groupHeader?.remove();
    const backButton = ritualView.querySelector(`button[onclick*="/g/${codigo}"]`);
    backButton?.remove();
};

const removeHomeLegacySections = (codigo) => {
    const movimiento = document.getElementById('dots-movimiento')?.closest('.mb-4');
    movimiento?.remove();
    const prodeButton = document.querySelector(`button[onclick*="/prode/${codigo}"]`);
    prodeButton?.closest('.mb-4')?.remove();
};

// 3.11 — resuelve DT-3: el guardian que sustituye el botón shortcut por el
// embed leía el tab del default 'arengas', sobrescribiendo el render que el
// router pintó para /rituales/<codigo>/<otro tab>.
const getActiveRitualTab = () => {
    const hash = window.location.hash || '';
    const m = hash.match(/^#\/rituales\/[^/]+\/([^/?]+)/);
    const valid = ['arengas', 'cabalas', 'promesas'];
    return (m && valid.includes(m[1])) ? m[1] : 'arengas';
};

// Generación de embed: un embed más nuevo (cambio de tab, abrir arenga)
// invalida a los anteriores que sigan resolviendo su promesa async.
let embedGeneration = 0;

// Embebe la vista de rituales en el home. async-aware (Bug #1): pinta un
// skeleton al instante y, cuando renderRitualesGrupo resuelve, reemplaza el
// skeleton por las cards reales DENTRO del slot — sin pisar el home.
const embedRituales = async (codigo, activeTab = 'arengas', options = {}) => {
    const root = document.getElementById('app-root');
    if (!root || typeof renderRitualesGrupo !== 'function') return;

    // Slot del embed: el clone ya presente (re-embed por cambio de tab /
    // acción) o el bloque del botón "Rituales del grupo" que pintó renderGrupo.
    let slot = root.querySelector('[data-rituales-grupo-clone]');
    const primerEmbed = !slot;
    if (!slot) {
        const ritualButton = Array.from(root.querySelectorAll('button[onclick*="/rituales/"]'))
            .find((b) => (b.getAttribute('onclick') || '').includes('/arengas'));
        const block = ritualButton?.closest('.mb-4') || ritualButton?.parentElement;
        if (!block) return;
        slot = document.createElement('section');
        slot.dataset.ritualesGrupoClone = 'true';
        slot.style.margin = '0 0 1rem';
        block.replaceWith(slot);
    }
    removeHomeLegacySections(codigo);

    const generation = ++embedGeneration;
    // Skeleton SOLO en la primera carga del grupo. En un cambio de tab los
    // datos ya están en caché → renderRitualesGrupo resuelve al instante;
    // mostrar el skeleton ahí sería un parpadeo innecesario (criterio Bug #1).
    if (primerEmbed) slot.innerHTML = buildRitualesSkeleton();

    // Arenga abierta / hoja de creación: renderRitualesGrupo las lee del hash.
    const query = options.arengaId ? `?arenga=${encodeURIComponent(options.arengaId)}`
        : options.crear ? '?crear=1' : '';
    const originalHash = window.location.hash;
    if (query) history.replaceState(null, '', `${location.pathname}${location.search}#/g/${codigo}${query}`);

    try {
        // renderRitualesGrupo pinta dentro del slot (no en #app-root) → no pisa
        // el home. Al resolver, su innerHTML reemplaza al skeleton en el lugar.
        await renderRitualesGrupo(codigo, activeTab, { target: slot, skipLoadingState: true });
        if (generation !== embedGeneration) return; // un embed más nuevo lo reemplazó
        const ritualView = slot.querySelector('.view-enter');
        if (ritualView) {
            stripEmbeddedRitualHeader(ritualView, codigo);
            slot.replaceChildren(ritualView);
        }
        retargetEmbeddedRituals(codigo, activeTab);
    } catch (err) {
        if (generation !== embedGeneration) return;
        console.error('[guardian] el embed de rituales falló:', err);
        slot.innerHTML = buildRitualesError();
        slot.querySelector('[data-rituales-retry]')
            ?.addEventListener('click', () => embedRituales(codigo, activeTab));
    } finally {
        if (query) history.replaceState(null, '', `${location.pathname}${location.search}${originalHash || `#/g/${codigo}`}`);
    }
};

let homeObserverPM = null;
// Observer de discovery (Bug #1 fix): F1.3.b volvió async el bootstrap de
// main.js — renderGrupo se pinta cientos de ms (a veces segundos) después
// del DOMContentLoaded. patchProfileRoute lo usa para esperar ese paint
// tardío en vez de abandonar tras unos frames de rAF. Ver el comentario allí.
let discoveryObserverPM = null;
const watchHomeRituals = (codigo) => {
    const root = document.getElementById('app-root');
    if (!root || typeof MutationObserver !== 'function') return;
    homeObserverPM?.disconnect();
    // Scan inicial — salvo en /rituales/* (esa ruta tiene su propio view).
    if (!window.location.hash.slice(1).startsWith('/rituales/')) {
        embedRituales(codigo, getActiveRitualTab());
        removeHomeLegacySections(codigo);
    }
    homeObserverPM = new MutationObserver(() => {
        // 3.11 — DT-3: el observer no opera en /rituales/<codigo>/<tab>, donde
        // renderRitualesGrupo pinta sus propios botones que confundirían al
        // matcher. Filtro negativo: todas las rutas EXCEPTO /rituales/.
        if (window.location.hash.startsWith('#/rituales/')) return;
        // El embed se inserta UNA vez: el skeleton lleva data-rituales-grupo-clone
        // sincrónicamente, así que tras el primer embedRituales el observer ya
        // no re-dispara (cubre el race router → renderGrupo → guardian).
        if (document.getElementById('dots-partidos') && !document.querySelector('[data-rituales-grupo-clone]')) {
            embedRituales(codigo, getActiveRitualTab());
        }
        removeHomeLegacySections(codigo);
    });
    homeObserverPM.observe(root, { childList: true, subtree: true });
};

const patchProfileRoute = () => {
    // Bug #1 fix — discovery resiliente. F1.3.b volvió el bootstrap de
    // main.js async (initAuth + cargarMisGrupos contra Postgres): renderGrupo
    // pinta el botón shortcut cientos de ms — a veces segundos — después del
    // DOMContentLoaded. El guardian NO puede abandonar tras unos frames de
    // rAF (el race original: sondeaba a t≈130ms, el home pintaba a t≈3400ms).
    // En su lugar: mientras no aparezca el código del grupo, observa
    // #app-root y se reintenta en cada mutación del DOM hasta detectar el
    // paint, tarde lo que tarde. hashchange / load / el setTimeout inicial
    // lo siguen disparando igual.
    const hash = window.location.hash.slice(1);
    // /rituales/* tiene su propio view; no inyectar embed home.
    if (hash.startsWith('/rituales/')) {
        discoveryObserverPM?.disconnect();
        discoveryObserverPM = null;
        homeObserverPM?.disconnect();
        return;
    }
    const ritualButton = Array.from(document.querySelectorAll('button[onclick*="/rituales/"]'))
        .find((b) => (b.getAttribute('onclick') || '').includes('/arengas'));
    const codigoFromDOM = ritualButton?.getAttribute('onclick')?.match(/\/rituales\/([^/]+)\/arengas/)?.[1];
    const codigoFromHash = hash.startsWith('/g/') ? hash.split('/')[2] : null;
    const codigoActivo = codigoFromHash || codigoFromDOM;
    if (codigoActivo) {
        // El home ya está pintado → el discovery terminó. watchHomeRituals
        // arma el embed y su propio observer de re-embed (homeObserverPM).
        discoveryObserverPM?.disconnect();
        discoveryObserverPM = null;
        watchHomeRituals(codigoActivo);
        return;
    }
    // Sin código todavía: o el home aún no se pintó (bootstrap async en
    // curso), o es una ruta sin grupo (/ingreso, /landing). Sin grupo no hay
    // re-embed que vigilar.
    homeObserverPM?.disconnect();
    // Observar #app-root y re-correr el discovery en cada mutación: el paint
    // tardío de renderGrupo se detecta apenas ocurre. El observer se
    // desconecta arriba apenas hay código.
    if (!discoveryObserverPM && typeof MutationObserver === 'function') {
        const root = document.getElementById('app-root');
        if (root) {
            discoveryObserverPM = new MutationObserver(patchProfileRoute);
            discoveryObserverPM.observe(root, { childList: true, subtree: true });
        }
    }
};

// Autoinicialización — solo en navegador (guard para que el módulo se pueda
// importar en entorno de tests sin DOM).
if (typeof window !== 'undefined') {
    // Hook que usa rituales.js (reRenderTarget) para re-embeber la vista tras
    // una acción (crear/resolver/validar/extender/eliminar/chat) en lugar de
    // pintar la vista aislada y pisar el home. Re-embed = mantiene el home
    // montado y la flecha ← funcional (fix BUG-1/2/3 navegación al crear ritual).
    window.__pmReembedRituales = (codigo, activeTab = 'arengas') => embedRituales(codigo, activeTab);
    window.addEventListener('hashchange', patchProfileRoute);
    window.addEventListener('load', () => {
        window.setTimeout(patchProfileRoute, 0);
    });
    window.setTimeout(patchProfileRoute, 0);
}
